// Components/Tabs/NewLabTab.jsx

import React from "react";
import PropTypes from "prop-types";

// Importing components
import Slider from "../Slider/Slider";
import SliderForm from "../SliderForm/SliderForm.jsx";
import useAdminToast from "../useAdminToast";
import Backend from "../../../utils/api.js";
import { useDocumentTitle } from "../../../utils/useDocumentTitle";

/**
 * NewLabTab Component
 * 
 * This component provides a form for creating a new lab. It handles form submission,
 * validates the lab abbreviation, and confirms completion with a toast.
 * 
 * @param {Object} props - Component properties.
 * @param {Object} props.user - The current user object containing user details.
 * @param {Function} props.pushTab - Function to push a new tab onto the stack.
 * @param {Function} props.revert - Function to revert the tab stack by a specified number of steps.
 * @param {number} props.index - The current index in the tab stack for proper stacking order.
 */
function NewLabTab({ user, pushTab, revert, index, onLabChanged }) { // Added 'index' prop

  const toast = useAdminToast();
  useDocumentTitle("Admin: Create New Space Tab – Pinpoint");

  return (
    <Slider
      name="New space"
      slide={true}
      navColor="rgb(255, 238, 83)"
      onClick={() => revert(1)} // Revert one step when navigation bar is clicked
      index={index} // Use dynamic index prop
    >
      {/* SliderForm displays the registration form for a new lab */}
      <SliderForm
        title="Register"
        actionLabel="Create space"
        fields={[
          {
            key: "name",
            placeholder: "Name (e.g. Iribe Sandbox)",
            validate: /^[A-Za-z0-9]+(?:\s[A-Za-z0-9]+)*$/,
            errorText:
              "Must be at least one word and have no leading or trailing spaces.",
          },
          {
            key: "id",
            placeholder: "Abbreviation (e.g. MIC)",
            validate: /^[A-Z0-9]{3}$/,
            errorText:
              "Must be three capital letters or numbers, with no leading or trailing spaces",
          },
        ]}
        onSubmit={async (values) => {
          const existing = await Backend.getLabById({ id: values.id, UID: user.UID });
          if (existing.status === 200) {
            throw new Error(`Space abbreviation ${values.id} is already used. Choose another abbreviation.`);
          }
          if (existing.status !== 404) {
            throw new Error("Could not check this abbreviation. Try again.");
          }
          const response = await Backend.newLab({ ...values, UID: user.UID });
          if (response.status !== 201) throw new Error("Could not create this space. Try again.");
          onLabChanged?.();
          toast({ title: "Space created" });
          revert(1);
        }}
      />
    </Slider>
  );
}

// PropTypes for type-checking component props
NewLabTab.propTypes = {
  user: PropTypes.object.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  onLabChanged: PropTypes.func,
};

export default NewLabTab;
